/**
 * Copied from shadc/ui on 12/16/2024
 * @see {@link https://ui.shadcn.com/docs/components/avatar}
 *
 * This component was updated to support the variants and match the styles from
 * the Figma design:
 * @see {@link https://www.figma.com/design/WfqIgsTFXN2BscBSSyXWF8/Coder-kit?node-id=711-383&t=xqxOSUk48GvDsjGK-0}
 *
 * It was also simplified to make usage easier and reduce boilerplate.
 * @see {@link https://github.com/coder/coder/pull/15930#issuecomment-2552292440}
 */
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import { Avatar as AvatarPrimitive } from "radix-ui";
import { useAppearance } from "#/theme/appearance";
import { getExternalImageStylesFromUrl } from "#/theme/externalImages";

const graphemeSegmenter =
	typeof Intl.Segmenter === "function"
		? new Intl.Segmenter("en", { granularity: "grapheme" })
		: undefined;

const avatarVariants = cva(
	"relative flex shrink-0 overflow-hidden rounded border border-solid bg-surface-secondary text-content-secondary",
	{
		variants: {
			size: {
				lg: "size-(--avatar-lg) rounded-[6px] text-sm font-medium",
				md: "size-(--avatar-default) text-2xs",
				sm: "size-(--avatar-sm) text-[8px]",
				xs: "size-(--avatar-xs) text-[6px]",
			},
			variant: {
				default: null,
				icon: "[&_svg]:size-full",
			},
		},
		defaultVariants: {
			size: "md",
		},
		compoundVariants: [
			{
				size: "lg",
				variant: "icon",
				className: "p-2",
			},
			{
				size: "md",
				variant: "icon",
				className: "p-1",
			},
			{
				size: "sm",
				variant: "icon",
				className: "p-[3px]",
			},
			{
				size: "xs",
				variant: "icon",
				className: "p-0.5",
			},
		],
	},
);

export type AvatarProps = AvatarPrimitive.AvatarProps &
	VariantProps<typeof avatarVariants> & {
		src?: string;
		fallback?: string;
		/**
		 * Alt text for the inner `<img>`. Defaults to `""` (decorative,
		 * hidden from assistive tech). Pass a descriptive value when no
		 * adjacent text identifies the content.
		 */
		alt?: string;
		ref?: React.Ref<React.ComponentRef<typeof AvatarPrimitive.Root>>;
	};

export const Avatar: React.FC<AvatarProps> = ({
	className,
	size,
	variant,
	src,
	fallback,
	alt = "",
	children,
	style,
	...props
}) => {
	const { externalImages } = useAppearance();

	const fallbackCharacters = fallback
		? graphemeSegmenter
			? Array.from(
					graphemeSegmenter.segment(fallback),
					({ segment }) => segment,
				)
			: Array.from(fallback)
		: [];
	const isEmoji = src?.startsWith("/emojis/");
	const avatarSizeToken =
		size === "lg" || size === "sm" || size === "xs" ? size : "default";

	return (
		<AvatarPrimitive.Root
			className={cn(
				avatarVariants({
					size,
					variant: isEmoji ? "default" : variant,
					className,
				}),
			)}
			style={{
				...style,
				padding: isEmoji
					? `calc(var(--avatar-${avatarSizeToken}) * 0.2)`
					: style?.padding,
			}}
			{...props}
		>
			<AvatarPrimitive.Image
				src={src}
				alt={alt}
				className="aspect-square size-full object-contain"
				style={getExternalImageStylesFromUrl(externalImages, src)}
			/>
			{fallback && (
				<AvatarPrimitive.Fallback className="flex h-full w-full items-center justify-center rounded-full">
					{fallbackCharacters.slice(0, 2).join("").toUpperCase()}
				</AvatarPrimitive.Fallback>
			)}
			{children}
		</AvatarPrimitive.Root>
	);
};
